import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CreditCard, FileText, Loader2, RefreshCw, WalletCards } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const money = (pence) => `£${((Number(pence) || 0) / 100).toFixed(2)}`;
const when = (value) => value ? new Date(value).toLocaleDateString("en-GB", {
  day: "numeric", month: "short", year: "numeric",
}) : "";

export default function PupilFinancialHistory({ endpoint, title = "Payments & receipts" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}${endpoint}`, { withCredentials: true });
      setData(response.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex justify-center py-5"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>
  );
  if (!data) return null;

  const movements = data.credit_movements || [];
  const payments = data.payments || [];

  return (
    <section data-testid="pupil-financial-history" className="space-y-3">
      <div className="rounded-2xl bg-gradient-to-br from-violet-600 to-blue-600 p-5 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-white/70">Credit basket</p>
            <p className="mt-1 text-3xl font-extrabold">{money(data.credit_balance_pence)}</p>
            <p className="mt-1 text-xs text-white/75">Automatically applied to the next unpaid lesson</p>
          </div>
          <WalletCards className="h-9 w-9 text-white/80" />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-extrabold text-slate-900 dark:text-white">{title}</h3>
        <button type="button" onClick={load} aria-label="Refresh payment history" className="p-2 text-slate-400">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {payments.length === 0 && movements.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800">
          No payments or credit movements yet.
        </div>
      ) : (
        <div className="space-y-2">
          {payments.map((payment) => (
            <article key={payment.id} className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-emerald-50 p-2 text-emerald-700"><CreditCard className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex justify-between gap-3">
                    <p className="font-bold text-slate-900 dark:text-white">Lesson payment</p>
                    <p className="font-extrabold text-slate-900 dark:text-white">{money(payment.receipt?.amount_pence)}</p>
                  </div>
                  <p className="text-xs text-slate-500">{when(payment.receipt?.paid_at)} · {payment.payment_method || "Payment"}</p>
                  <div className="mt-2 flex items-center gap-1 text-xs font-bold text-emerald-700">
                    <FileText className="h-3.5 w-3.5" />
                    Receipt {payment.receipt?.receipt_number}
                  </div>
                </div>
              </div>
            </article>
          ))}
          {movements.map((entry) => (
            <article key={entry.id} className="rounded-2xl border border-violet-200 bg-violet-50/60 p-4 dark:border-violet-800 dark:bg-violet-900/10">
              <div className="flex justify-between gap-3">
                <div>
                  <p className="font-bold text-violet-800 dark:text-violet-300">
                    {entry.amount_pence > 0 ? "Credit added" : "Credit applied"}
                  </p>
                  <p className="text-xs text-slate-500">{entry.reason} · {when(entry.created_at)}</p>
                </div>
                <p className="font-extrabold text-violet-700 dark:text-violet-300">
                  {entry.amount_pence > 0 ? "+" : "−"}{money(Math.abs(entry.amount_pence))}
                </p>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
